/**
 * Links para receber com token opaco (backend Firestore).
 * O endereço e dados sensíveis ficam no Firestore; o link/QR contém apenas o ID.
 */

import { db } from './firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

const COLLECTION = 'paymentRequests';
const DEFAULT_TTL_DAYS = 7;

function generateToken() {
  return crypto.randomUUID();
}

/**
 * Cria um payment request no Firestore e retorna o token opaco.
 * @param {object} payload - { to, amount, token, network, currency, receiverProfile?: { name, email, phone, photoURL, network, networkName } }
 * @param {number} [ttlDays] - dias até expirar (default 7)
 * @returns {Promise<string>} token (id) para usar na URL/QR
 */
export async function createPaymentRequest(payload, ttlDays = DEFAULT_TTL_DAYS) {
  if (!db) throw new Error('Firebase não disponível.');
  const { to, amount, token, network, currency, receiverProfile } = payload;
  if (!to || amount == null) throw new Error('to e amount são obrigatórios.');

  const id = generateToken();
  const now = new Date();
  const expiresAt = new Date(now.getTime() + ttlDays * 24 * 60 * 60 * 1000);

  const docData = {
    to: String(to).trim(),
    amount: String(amount),
    token: token || '',
    network: network || '',
    currency: currency === 'BRL' ? 'BRL' : 'USD',
    createdAt: serverTimestamp(),
    expiresAt: expiresAt.toISOString(),
  };
  if (receiverProfile != null && typeof receiverProfile === 'object') {
    docData.receiverProfile = {
      name: receiverProfile.name ?? '',
      email: receiverProfile.email ?? '',
      phone: receiverProfile.phone ?? '',
      photoURL: receiverProfile.photoURL ?? '',
      network: receiverProfile.network ?? '',
      networkName: receiverProfile.networkName ?? '',
    };
  }

  await setDoc(doc(db, COLLECTION, id), docData);

  return id;
}

/**
 * Resolve um token opaco e retorna o payload (ou null se não existir/expirado).
 * @param {string} id - token retornado por createPaymentRequest
 * @returns {Promise<{ to, amount, token, network, currency, receiverProfile? } | null>}
 */
export async function getPaymentRequest(id) {
  if (!db || !id || typeof id !== 'string') return null;
  const trimmed = id.trim();
  if (!trimmed) return null;

  const snap = await getDoc(doc(db, COLLECTION, trimmed));
  if (!snap.exists()) return null;

  const data = snap.data();
  const expiresAt = data.expiresAt ? new Date(data.expiresAt) : null;
  if (expiresAt && expiresAt.getTime() < Date.now()) return null;

  const result = {
    to: data.to || '',
    amount: data.amount != null ? String(data.amount) : '',
    token: data.token || '',
    network: data.network || '',
    currency: data.currency === 'BRL' ? 'BRL' : 'USD',
  };
  if (data.receiverProfile && typeof data.receiverProfile === 'object') {
    result.receiverProfile = {
      address: data.to || '',
      name: data.receiverProfile.name ?? '',
      email: data.receiverProfile.email ?? '',
      phone: data.receiverProfile.phone ?? '',
      photoURL: data.receiverProfile.photoURL ?? '',
      network: data.receiverProfile.network ?? '',
      networkName: data.receiverProfile.networkName ?? '',
    };
  }
  return result;
}
